import type { UseMutationOptions, UseMutationResult, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { renameChat } from 'tripPlanner/api/v3'
import {
  claimChats,
  deleteChat,
  generateChatTitle,
  getChat,
  getItinerary,
  getPlaceAreas,
  getPlaceBestTimeToVisit,
  getPlaceDetail,
  getPlaceInfo,
  getPlaceInsiderTips,
  getPlaceOverview,
  getPlaceWhatToLove,
  listChats,
  switchDefaultTransport,
  updateChat,
} from 'tripPlanner/api'
import type {
  ChatListResponse,
  ClaimChatsRequest,
  ClaimChatsResponse,
  DeleteChatResponse,
  GenerateChatTitleResponse,
  GetChatResponse,
  GetItineraryResponse,
  GetPlaceAreasResult,
  GetPlaceBestTimeToVisitResult,
  GetPlaceDetailResult,
  GetPlaceInfoResult,
  GetPlaceInsiderTipsResult,
  GetPlaceOverviewResult,
  GetPlaceWhatToLoveResult,
  SwitchDefaultTransportResponse,
  UpdateChatRequest,
  UpdateChatResponse,
} from 'tripPlanner/api/types'
import type { RenameChatResult } from 'tripPlanner/api/v3'
import * as ChatKeys from '../reactQueryKeys/chat'
import * as PlaceInfoKeys from '../reactQueryKeys/placeInfo'

export type GetChatResult = GetChatResponse
export const CHAT_CLAIM_403_RETRY_DELAY_MS = 1000
export const CHAT_CLAIM_403_MAX_RETRIES = 2

export function isChatClaim403RetryableError(error: unknown): error is { status: number } {
  return error !== null &&
    typeof error === 'object' &&
    'status' in error &&
    typeof error.status === 'number' &&
    error.status === 403
}

export const useGetChat = (
  chatId: number | undefined,
  options?: Omit<UseQueryOptions<GetChatResult, unknown, GetChatResult, Array<string | number>>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GetChatResult, unknown> => {
  return useQuery({
    queryKey: ChatKeys.detail(chatId ?? 0),
    queryFn: async() => {
      if (!chatId) {
        throw new Error('Chat ID is required')
      }
      return getChat(chatId)
    },
    enabled: !!chatId,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    staleTime: 0,
    retry: (failureCount, error) => {
      if (failureCount >= CHAT_CLAIM_403_MAX_RETRIES) return false
      return isChatClaim403RetryableError(error)
    },
    retryDelay: CHAT_CLAIM_403_RETRY_DELAY_MS,
    ...options,
  })
}

export type ChatListResult = ChatListResponse

export const useChatList = (
  options?: Omit<UseQueryOptions<ChatListResult, unknown, ChatListResult, Array<string>>, 'queryKey' | 'queryFn'>,
): UseQueryResult<ChatListResult, unknown> => {
  return useQuery({
    queryKey: ChatKeys.list,
    queryFn: async() => listChats(),
    ...options,
  })
}

export type DeleteChatResult = DeleteChatResponse

export const useDeleteChatMutation = (
  options?: UseMutationOptions<DeleteChatResult, unknown, { chatId: number }>,
): UseMutationResult<DeleteChatResult, unknown, { chatId: number }> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async({ chatId }) => deleteChat(chatId),
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.removeQueries({ queryKey: ChatKeys.detail(variables.chatId) })
      queryClient.removeQueries({ queryKey: ChatKeys.itineraryDetail(variables.chatId) })
      options?.onSuccess?.(data, variables, context)
    },
  })
}

export type SwitchDefaultTransportResult = SwitchDefaultTransportResponse

export const useSwitchDefaultTransportMutation = (
  options?: UseMutationOptions<SwitchDefaultTransportResult, unknown, { itemId: number }>,
): UseMutationResult<SwitchDefaultTransportResult, unknown, { itemId: number }> => {
  return useMutation({
    mutationFn: async({ itemId }) => switchDefaultTransport(itemId),
    ...options,
  })
}

export type ClaimChatsResult = ClaimChatsResponse

export const useClaimChatsMutation = (
  options?: UseMutationOptions<ClaimChatsResult, unknown, ClaimChatsRequest>,
): UseMutationResult<ClaimChatsResult, unknown, ClaimChatsRequest> => {
  return useMutation({
    mutationFn: async(params) => claimChats(params),
    ...options,
  })
}

export type GenerateChatTitleParams = {
  chatId: number
  message: string
}

export type GenerateChatTitleResult = GenerateChatTitleResponse

export const useGenerateChatTitleMutation = (
  options?: UseMutationOptions<
    GenerateChatTitleResult,
    unknown,
    GenerateChatTitleParams
  >,
): UseMutationResult<
  GenerateChatTitleResult,
  unknown,
  GenerateChatTitleParams
> => {
  return useMutation({
    mutationFn: async(params) => generateChatTitle(params),
    ...options,
  })
}

export type UpdateChatParams = { chatId: number } & UpdateChatRequest
export type UpdateChatResult = UpdateChatResponse

export const useUpdateChatMutation = (
  options?: UseMutationOptions<UpdateChatResult, unknown, UpdateChatParams>,
): UseMutationResult<UpdateChatResult, unknown, UpdateChatParams> => {
  return useMutation({
    mutationFn: async(params) => updateChat(params),
    ...options,
  })
}

export type RenameChatParams = { chatId: number; name: string }
export type RenameChatMutationResult = RenameChatResult

export const useRenameChatMutation = (
  options?: UseMutationOptions<
    RenameChatMutationResult,
    unknown,
    RenameChatParams,
    {
      previousChatList: ChatListResult | undefined
      previousChatDetail: GetChatResult | undefined
    }
  >,
): UseMutationResult<
  RenameChatMutationResult,
  unknown,
  RenameChatParams,
  {
    previousChatList: ChatListResult | undefined
    previousChatDetail: GetChatResult | undefined
  }
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async(params) => renameChat(params),
    ...options,
    onMutate: async({ chatId, name }) => {
      await queryClient.cancelQueries({ queryKey: ChatKeys.list })
      await queryClient.cancelQueries({ queryKey: ChatKeys.detail(chatId) })

      const previousChatList = queryClient.getQueryData<ChatListResult>(ChatKeys.list)
      const previousChatDetail = queryClient.getQueryData<GetChatResult>(ChatKeys.detail(chatId))

      queryClient.setQueryData<ChatListResult | undefined>(ChatKeys.list, (previous) => {
        if (!previous) return previous
        return previous.map(chat => chat.id === chatId ? { ...chat, name } : chat)
      })
      queryClient.setQueryData<GetChatResult | undefined>(ChatKeys.detail(chatId), (previous) => {
        if (!previous) return previous
        return { ...previous, name }
      })

      const context = { previousChatList, previousChatDetail }
      await options?.onMutate?.({ chatId, name })
      return context
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(ChatKeys.list, context?.previousChatList)
      queryClient.setQueryData(ChatKeys.detail(variables.chatId), context?.previousChatDetail)
      options?.onError?.(error, variables, context)
    },
    onSuccess: (data, variables, context) => {
      const updatedName = data.name ?? variables.name
      queryClient.setQueryData<ChatListResult | undefined>(ChatKeys.list, (previous) => {
        if (!previous) return previous
        return previous.map(chat => chat.id === variables.chatId ? { ...chat, name: updatedName, updatedAt: data.updatedAt } : chat)
      })
      queryClient.setQueryData<GetChatResult | undefined>(ChatKeys.detail(variables.chatId), (previous) => {
        if (!previous) return previous
        return { ...previous, name: updatedName, updatedAt: data.updatedAt }
      })
      options?.onSuccess?.(data, variables, context)
    },
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ChatKeys.list })
      queryClient.invalidateQueries({ queryKey: ChatKeys.detail(variables.chatId) })
      options?.onSettled?.(data, error, variables, context)
    },
  })
}

export type GetItineraryResult = GetItineraryResponse['result']

export const useGetItinerary = (
  chatId: number | undefined,
  options?: Omit<UseQueryOptions<GetItineraryResult, unknown, GetItineraryResult, Array<string | number>>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GetItineraryResult, unknown> => {
  return useQuery({
    queryKey: ChatKeys.itineraryDetail(chatId ?? 0),
    queryFn: async() => {
      if (!chatId) {
        throw new Error('Chat ID is required')
      }
      return getItinerary(chatId)
    },
    enabled: !!chatId,
    refetchOnWindowFocus: true,
    staleTime: 0,
    ...options,
  })
}

export const useGetPlaceDetail = (
  googlePlaceId: string | undefined,
  options?: Omit<UseQueryOptions<GetPlaceDetailResult, unknown, GetPlaceDetailResult, Array<string>>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GetPlaceDetailResult, unknown> => {
  return useQuery({
    queryKey: PlaceInfoKeys.detail(googlePlaceId ?? ''),
    queryFn: async() => {
      if (!googlePlaceId) {
        throw new Error('Google Place ID is required')
      }
      return getPlaceDetail(googlePlaceId)
    },
    enabled: !!googlePlaceId,
    staleTime: Infinity,
    ...options,
  })
}

// Place Detail Section Hooks - for progressive loading

export const useGetPlaceOverview = (
  googlePlaceId: string | undefined,
  options?: Omit<UseQueryOptions<GetPlaceOverviewResult, unknown, GetPlaceOverviewResult, Array<string>>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GetPlaceOverviewResult, unknown> => {
  return useQuery({
    queryKey: PlaceInfoKeys.overview(googlePlaceId ?? ''),
    queryFn: async() => {
      if (!googlePlaceId) {
        throw new Error('Google Place ID is required')
      }
      return getPlaceOverview(googlePlaceId)
    },
    enabled: !!googlePlaceId,
    staleTime: Infinity,
    ...options,
  })
}

export const useGetPlaceBestTimeToVisit = (
  googlePlaceId: string | undefined,
  options?: Omit<UseQueryOptions<GetPlaceBestTimeToVisitResult, unknown, GetPlaceBestTimeToVisitResult, Array<string>>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GetPlaceBestTimeToVisitResult, unknown> => {
  return useQuery({
    queryKey: PlaceInfoKeys.bestTimeToVisit(googlePlaceId ?? ''),
    queryFn: async() => {
      if (!googlePlaceId) {
        throw new Error('Google Place ID is required')
      }
      return getPlaceBestTimeToVisit(googlePlaceId)
    },
    enabled: !!googlePlaceId,
    staleTime: Infinity,
    ...options,
  })
}

export const useGetPlaceWhatToLove = (
  googlePlaceId: string | undefined,
  options?: Omit<UseQueryOptions<GetPlaceWhatToLoveResult, unknown, GetPlaceWhatToLoveResult, Array<string>>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GetPlaceWhatToLoveResult, unknown> => {
  return useQuery({
    queryKey: PlaceInfoKeys.whatToLove(googlePlaceId ?? ''),
    queryFn: async() => {
      if (!googlePlaceId) {
        throw new Error('Google Place ID is required')
      }
      return getPlaceWhatToLove(googlePlaceId)
    },
    enabled: !!googlePlaceId,
    staleTime: Infinity,
    ...options,
  })
}

export const useGetPlaceAreas = (
  googlePlaceId: string | undefined,
  options?: Omit<UseQueryOptions<GetPlaceAreasResult, unknown, GetPlaceAreasResult, Array<string>>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GetPlaceAreasResult, unknown> => {
  return useQuery({
    queryKey: PlaceInfoKeys.areas(googlePlaceId ?? ''),
    queryFn: async() => {
      if (!googlePlaceId) {
        throw new Error('Google Place ID is required')
      }
      return getPlaceAreas(googlePlaceId)
    },
    enabled: !!googlePlaceId,
    staleTime: Infinity,
    ...options,
  })
}

export const useGetPlaceInsiderTips = (
  googlePlaceId: string | undefined,
  options?: Omit<UseQueryOptions<GetPlaceInsiderTipsResult, unknown, GetPlaceInsiderTipsResult, Array<string>>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GetPlaceInsiderTipsResult, unknown> => {
  return useQuery({
    queryKey: PlaceInfoKeys.insiderTips(googlePlaceId ?? ''),
    queryFn: async() => {
      if (!googlePlaceId) {
        throw new Error('Google Place ID is required')
      }
      return getPlaceInsiderTips(googlePlaceId)
    },
    enabled: !!googlePlaceId,
    staleTime: Infinity,
    ...options,
  })
}

export const useGetPlaceInfo = (
  googlePlaceId: string | undefined,
  options?: Omit<UseQueryOptions<GetPlaceInfoResult, unknown, GetPlaceInfoResult, Array<string>>, 'queryKey' | 'queryFn'>,
): UseQueryResult<GetPlaceInfoResult, unknown> => {
  return useQuery({
    queryKey: PlaceInfoKeys.info(googlePlaceId ?? ''),
    queryFn: async() => {
      if (!googlePlaceId) {
        throw new Error('Google Place ID is required')
      }
      return getPlaceInfo(googlePlaceId)
    },
    enabled: !!googlePlaceId,
    staleTime: Infinity,
    ...options,
  })
}
